import React, { useCallback } from 'react';
import Button from '@ui/Button';
import PrimaryButtonWithDropdown, {
  PrimaryButtonWithDropdownProps,
} from '@ui/Button/legacy/PrimaryButtonWithDropdown';
import { EditorState } from '../../../store/EditorSlice';
import CircularLoader from '../../LoadingIndicators/CircularLoader';
import { useStyles } from '../../StudioClipPreview/PreviewButton';

const RenderModeType = {
  all: 'Create single take',
  paragraph: 'Render by paragraph',
  sentence: 'Render by sentence',
} as const;

export interface EditorPrimaryActionButtonProps {
  onButtonClick: (value?: any) => void;
  onDropdownSelect: PrimaryButtonWithDropdownProps['onDropdownSelect'];
  loading?: boolean;
  editorState: EditorState;
  createCancelButtonCooldown?: boolean;
  selectDropdownDisabled?: boolean;
  isCuesMode: boolean;
  disabled: boolean;
}

const EditorPrimaryActionButton = ({
  onButtonClick,
  onDropdownSelect,
  loading,
  editorState,
  createCancelButtonCooldown,
  selectDropdownDisabled,
  isCuesMode,
  disabled,
}: EditorPrimaryActionButtonProps) => {
  const classes = useStyles();
  const valueToDisplayString = useCallback(
    (value: keyof typeof RenderModeType) => {
      return RenderModeType[value];
    },
    []
  );

  const conditionalCuesContent = (
    <Button
      variant="secondary"
      onClick={onButtonClick}
      size="small"
      style={{ minWidth: '138px' }}
      disabled={createCancelButtonCooldown || disabled}
    >
      {loading ? (
        <>
          <CircularLoader className={classes.classes.loadingIcon} />
          {'Cancel'}
        </>
      ) : (
        <>Generate Preview</>
      )}
    </Button>
  );

  const primaryButtonDisabled =
    !editorState.characterLimit || createCancelButtonCooldown || disabled;

  return isCuesMode ? (
    conditionalCuesContent
  ) : (
    <PrimaryButtonWithDropdown
      id="render-options"
      onButtonClick={onButtonClick}
      onDropdownSelect={onDropdownSelect}
      data-e2e="project-editor-submit"
      color={loading ? 'secondary' : 'primary'}
      type="submit"
      primaryButtonDisabled={primaryButtonDisabled}
      loading={loading}
      loadingText={'Cancel'}
      selectedListItem={valueToDisplayString(editorState.renderMode)}
      selectDropdownDisabled={selectDropdownDisabled}
      selectDropdownButtonLabel="More render options"
      listItems={[
        {
          children: <div>Create single take</div>,
          value: RenderModeType.all,
          key: 'all',
          displayChildren: true,
        },
        {
          children: <div>Render by paragraph</div>,
          value: RenderModeType.paragraph,
          key: 'paragraph',
        },
        {
          children: <div>Render by sentence</div>,
          value: RenderModeType.sentence,
          key: 'sentence',
        },
      ]}
    >
      {'Create'}
    </PrimaryButtonWithDropdown>
  );
};

export default EditorPrimaryActionButton;
